import { Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SubheaderService } from '../../../../core/_base/layout';
import { AppState } from '../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../core/_base/crud';
import { Store, select } from '@ngrx/store';
import { debounceTime, delay, distinctUntilChanged, skip, take, tap } from 'rxjs/operators';
import { fromEvent, merge, of, Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort} from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';

import {
    LocationDescriptionsService,
    LocationDescription,
    LocationDescriptionsDataSource,
    LocationDescriptionsPageRequested,
    LocationDescriptionDeleted,
    selectTrashedLocationDescriptionCount,
    selectAdminTrashedLocationDescriptionCount,
    LocationDescriptionsActionToggleLoading,
    LocationDescriptionOnServerAdminRestored,
    LocationDescriptionOnServerRestored,
    LocationDescriptionDeletedFromTrash,
    LocationDescriptionDeletedFromAdminTrash,
    LocationDescriptionTrashFlushed,
    LocationDescriptionUpdated,
} from '../../../../core/linked-tables';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { cloneDeep } from 'lodash';
import { hasPermission } from 'src/app/core/mad-auth/mad-auth.store';


@Component({
    selector: 'kt-location-descriptions-list',
    templateUrl: './location-descriptions-list.component.html',
    styleUrls: ['./location-descriptions-list.component.scss']
})
export class LocationDescriptionsListComponent implements OnInit, OnDestroy {

    dataSource: LocationDescriptionsDataSource;
    displayedColumns = ['id', 'sub_community', 'community', 'location_description', 'is_hidden', 'actions'];

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild('sort1', {static: true}) sort: MatSort;
    // Filter fields
    @ViewChild('searchInput', {static: true}) searchInput: ElementRef;
    // Selection
    selection = new SelectionModel<LocationDescription>(true, []);
    locationDescriptionResult: LocationDescription[] = [];

    trashCnt: number = 0;
    adminTrashCnt: number = 0;

    // Subscriptions
    private subscriptions: Subscription[] = [];
    canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))

    /**
     * Component constructor
     *
     * @param dialog: MatDialog
     * @param snackBar: MatSnackBar
     * @param layoutUtilsService: LayoutUtilsService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
        private router: Router,
        public dialog: MatDialog,
        public snackBar: MatSnackBar,
        public service: LocationDescriptionsService,
        public subheaderService: SubheaderService,
        private layoutUtilsService: LayoutUtilsService,
        private translate: TranslateService,
        private store: Store<AppState>) {
    }

    /**
     * On init
     */
    ngOnInit() {
        const title = this.translate.instant('LOCATION_DESCRIPTION.LIST.TITLE');
        this.subheaderService.setTitle(title);
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title, page: `../default/admin-management/linked-tables/location-descriptions`}
        ]);

        this.paginator._intl.itemsPerPageLabel = 'Display';
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadLocationDescriptionsList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);

        // Filtration, bind to searchInput
        const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
            debounceTime(50),
            distinctUntilChanged(),
            tap(() => {
                this.paginator.pageIndex = 0;
                this.loadLocationDescriptionsList();
            })
        )
            .subscribe();
        this.subscriptions.push(searchSubscription);

        // Init DataSource
        this.dataSource = new LocationDescriptionsDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {
            this.locationDescriptionResult = res;
        });

        // trash count
        const selectTrashedSubscription = this.store.pipe(
            select(selectTrashedLocationDescriptionCount)
        ).subscribe(res => {
            this.trashCnt = res;
        });
        this.subscriptions.push(selectTrashedSubscription);

        const selectAdminTrashedSubscription = this.store.pipe(
            select(selectAdminTrashedLocationDescriptionCount)
        ).subscribe(res => {
            this.adminTrashCnt = res;
        });
        this.subscriptions.push(selectAdminTrashedSubscription);

        this.subscriptions.push(entitiesSubscription);
        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitation
            this.loadLocationDescriptionsList();
        }); // Remove this line, just loading imitation
    }

    /**
     * Load LocationDescriptions List from service through data-source
     */
    loadLocationDescriptionsList() {
        this.selection.clear();
        const queryParams = new QueryParamsModel(
            this.filterConfiguration(),
            this.sort.direction,
            this.sort.active,
            this.paginator.pageIndex + 1,
            this.paginator.pageSize
        );
        this.store.dispatch(new LocationDescriptionsPageRequested({ page: queryParams }));
        this.selection.clear();
    }

    /**
     * Returns object for filter
     */
    filterConfiguration(): any {
        const filter: any = {
            with_hidden: true
        };
        const searchText: string = this.searchInput.nativeElement.value;

        filter.name = searchText;
        if (!searchText) {
            return filter;
        }
        return filter;
    }

    /**
     * Returns object for filter
     */
    resetFilter(): any {
        this.searchInput.nativeElement.value = '';
        this.loadLocationDescriptionsList();
    }

    /**
     * Show add locationDescription dialog
     */
    addLocationDescription() {
        this.editLocationDescription(null);
    }

    /**
     * Show Edit locationDescription view and save after success close result
     * @param locationDescription: LocationDescription
     */
    editLocationDescription(_locationDescription) {
        if (_locationDescription)
            this.router.navigate(['edit', _locationDescription.id], {relativeTo: this.activatedRoute});
        else
            this.router.navigate(['add'], {relativeTo: this.activatedRoute});
    }

    deleteLocationDescription(_item: LocationDescription) {
        const _title = this.translate.instant('LOCATION_DESCRIPTION.LIST.DIALOG.DELETE.TITLE');
        const _description: string = this.translate.instant('LOCATION_DESCRIPTION.LIST.DIALOG.DELETE.DESCRIPTION', {name: this.itemLabel(_item)});
        const _waitDesciption = this.translate.instant('LOCATION_DESCRIPTION.LIST.DIALOG.DELETE.WAIT_DESCRIPTION');

        const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }

            this.store.dispatch(new LocationDescriptionDeleted({id: _item.id}));
        });
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(el => el.unsubscribe());
    }

    // Fetch trash data
    trash() {
        this.store.dispatch(new LocationDescriptionsActionToggleLoading({isLoading: true}));
        this.service.getTrashed().subscribe(res => {

            this.store.dispatch(new LocationDescriptionsActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: this.itemLabel(elem),
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.user_deleted,
                    deletedUser: (elem.userDeletedBy && elem.userDeletedBy.length > 0 ? elem.userDeletedBy : ''),
                });
            });
            this.show_trash(items);
        });
    }

    // Fetch admin trash data
    adminTrash() {
        this.store.dispatch(new LocationDescriptionsActionToggleLoading({isLoading: true}));
        this.service.getAdminTrashed().subscribe(res => {

            this.store.dispatch(new LocationDescriptionsActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: this.itemLabel(elem),
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.deleted_at,
                    deletedUser: (elem.deletedBy && elem.deletedBy.length > 0 ? elem.deletedBy : ''),
                });
            });
            this.show_trash(items, true);
        });
    }

    /**
     * Show Edit agency dialog and save after success close result
     * @param items
     * @param isAdmin
     */
    show_trash(items: any[], isAdmin = false) {
        let _title = this.translate.instant('LOCATION_DESCRIPTION.LIST.DIALOG.SHOW_TRASH.TITLE');
        if (isAdmin) {
            _title = this.translate.instant('LOCATION_DESCRIPTION.LIST.DIALOG.SHOW_TRASH.TITLE_ADMIN');
        }
        this.layoutUtilsService.trashedEntities(_title, items, this, isAdmin);
    }

    restore(id: number, isAdmin = false) {
        if (isAdmin) {
            this.store.dispatch(new LocationDescriptionOnServerAdminRestored({id}));
        } else {
            this.store.dispatch(new LocationDescriptionOnServerRestored({id}));
        }
    }

    delete(id: number) {
        this.store.dispatch(new LocationDescriptionDeletedFromTrash({id}));
    }

    hardDelete(id: number) {
        this.store.dispatch(new LocationDescriptionDeletedFromAdminTrash({id}));
    }

    flush() {
        this.store.dispatch(new LocationDescriptionTrashFlushed());
    }

    viewEntry(item: LocationDescription) {
        this.router.navigate(['view', item.id], {relativeTo: this.activatedRoute})
    }

    onHiddenChange(event: MatCheckboxChange, item: LocationDescription) {
        const _item = cloneDeep(item)
        _item.is_hidden = event.checked
        this.store.dispatch(new LocationDescriptionUpdated({
            locationDescription: _item,
            partialLocationDescription: {
                id: item.id,
                changes: {
                    is_hidden: event.checked
                }
            }
        }))
    }

    /**
     * The table has no single "name" column, so trash/dialog labels combine the two identifying fields.
     */
    private itemLabel(item: { sub_community?: string, community?: string }): string {
        return [item.sub_community, item.community].filter(part => part && part.length > 0).join(', ');
    }
}
